import { Box, Flex } from "@chakra-ui/layout";
import { Checkbox, Skeleton, Text } from "@chakra-ui/react";
import { Col, Row } from "antd";
import useCreateFracao from "features/fracao/hooks/useCreateFracao";
import useEditFracao from "features/fracao/hooks/useEditFracao.ts";
import useGetFracao from "features/fracao/hooks/useGetFracao";
import { FracaoForm } from "features/fracao/typings";
import useAllUnidades from "features/unidades/useAllUnidades";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import OutlineButton from "components/Button/OutlineButton";
import PrimaryButton from "components/Button/PrimaryButton";
import FracaoTable from "components/FracaoTable";
import FormFracaoHeader from "./FormFracaoHeader";
import colors from "resources/theme/colors";
import FracaoChart from "./FracaoChart";
import FracaoChartLegend from "./FracaoChartLegend";
import { applyPrecision } from "infra/helpers/applyPrecision";
import { showConfirm } from "infra/helpers/alerta";

function FormFracao({
  onSuccess,
  onClose,
  visible,
  fracaoEditId,
}: {
  onSuccess?: (data: any) => Promise<void>;
  onClose?: () => void;
  visible: boolean;
  fracaoEditId?: string;
}) {
  const {
    data: fracaoEditData,
    isLoading: isLoadingFracao,
    isFetching: isFetchingFracao,
  } = useGetFracao(fracaoEditId);

  const {
    data: allUnidades,
    isLoading: isLoadingAllUnidades,
    isFetching: isFetchingAllUnidades,
  } = useAllUnidades();

  const [splitEqually, setSplitEqually] = useState(false);
  const [filteredUnidades, setFilteredUnidades] = useState(
    allUnidades?.items ?? []
  );
  const [search, setSearch] = useState("");
  const [tableKey, setTableKey] = useState(+new Date());
  const [enabledEdit, setEnabledEdit] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState,
    watch,
    setValue,
    getValues,
  } = useForm<FracaoForm>({
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const { isLoading: isLoadingCreate, mutate: handleCreate } =
    useCreateFracao();
  const { isLoading: isLoadingEdit, mutate: handleEdit } = useEditFracao();

  const splitValueEqually = useCallback(
    (value: number, override: boolean = false) => {
      if (allUnidades && allUnidades?.items.length > 0) {
        const currentUnidades = getValues("unidades");
        const fraction = value / allUnidades?.items.length;

        setValue(
          "unidades",
          allUnidades.items.reduce((acc, next) => {
            const currentValue = currentUnidades[next.id] || 0;
            acc[next.id] = override ? fraction : currentValue + fraction;
            return acc;
          }, {} as Record<string, number>)
        );

        setTableKey(+new Date());
      }
    },
    [allUnidades, getValues, setValue]
  );

  useEffect(() => {
    if (splitEqually) {
      splitValueEqually(100, true);
    }
  }, [splitEqually, splitValueEqually]);

  useEffect(() => {
    if (fracaoEditId && fracaoEditData && allUnidades?.items) {
      reset({
        nome: fracaoEditData.nome,
        unidades: fracaoEditData.unidades.reduce((acc, next) => {
          acc[next.id] = next.valor;
          return acc;
        }, {} as Record<string, any>),
      });
      setFilteredUnidades(allUnidades.items ?? []);
      setTableKey(+new Date());
    } else if (allUnidades?.items && !isLoadingFracao) {
      reset({
        nome: "Fração genérica",
      });
      setTableKey(+new Date());
      setFilteredUnidades(allUnidades.items ?? []);
    }
  }, [allUnidades, fracaoEditData, fracaoEditId, isLoadingFracao, reset]);

  const onSubmit = async (data: FracaoForm) => {
    const mappedData = {
      nome: data.nome,
      unidades: Object.keys(data.unidades).map((key) => ({
        id: key,
        valor: data.unidades[key]
          ? applyPrecision(data.unidades[key], 8)
          : data.unidades[key],
      })),
    };
    if (fracaoEditData?.id) {
      showConfirm(
        "Deseja salvar a nova fração ideal? Obs: Caso exista fração ideal cadastrada em alguma unidade ela será substituida"
      ).then(({ isConfirmed }) => {
        isConfirmed &&
          handleEdit(
            { id: fracaoEditData.id, data: mappedData },
            { onSuccess: () => onSuccess?.(fracaoEditData.id) }
          );
      });
    } else {
      handleCreate(
        { data: mappedData },
        { onSuccess: ({ data }) => onSuccess?.(data?.id) }
      );
    }
  };

  const form = watch();

  const onSearch = (q: string | undefined) => {
    if (!allUnidades) {
      return;
    }

    if (!q) {
      setFilteredUnidades(allUnidades.items ?? []);
      return;
    }

    const filteredItems = allUnidades?.items.filter((u) => {
      let nomeUnidade = `Número ${u.numero}`;

      if (u.blocoQuadra) {
        nomeUnidade += ` - Bloco/Quadra ${u.blocoQuadra.nome}`;
      }

      const nomeProprietario = u.pessoa?.nome;

      return (
        nomeUnidade.toLowerCase().includes(q.toLowerCase()) ||
        nomeProprietario?.toLowerCase().includes(q.toLowerCase())
      );
    });
    setFilteredUnidades([...(allUnidades?.items ?? []), ...filteredItems]);
    setSearch(q);
  };

  const _isLoadingData =
    isLoadingFracao ||
    isLoadingAllUnidades ||
    isFetchingFracao ||
    isFetchingAllUnidades;

  const isLoadingMutate = isLoadingCreate || isLoadingEdit;

  const sumValues = form.unidades
    ? Number(Object.values(form.unidades).reduce((a, b) => a + b, 0))
    : 0;
  const remainingValue = 100 - sumValues;

  const _enabledEdit = enabledEdit && !splitEqually;

  const isOverLimit = remainingValue < -1e-9;
  return (
    <form
      onSubmit={(ev) => handleSubmit(onSubmit)(ev)}
      style={{ display: "flex", flex: 1, height: "100%" }}
    >
      <Flex flexDir="column" flex={1} h="100%" flexGrow={1}>
        <Flex mb={4} h="40px">
          <FormFracaoHeader
            onEnableEdit={() => setEnabledEdit((state) => !state)}
            onExcelInput={() => null}
            enabledEdit={enabledEdit}
            control={control}
            loading={_isLoadingData}
          />
        </Flex>

        <Row gutter={48}>
          <Box
            as={Col}
            sm={17}
            justifyContent="flex-end"
            display="flex"
            h="20px"
          >
            {!_isLoadingData ? (
              <Checkbox
                isChecked={splitEqually}
                onChange={() => setSplitEqually((state) => !state)}
              >
                <Text fontSize="15px" color="gray.700">
                  Distribuir igualmente
                </Text>
              </Checkbox>
            ) : null}
          </Box>
        </Row>
        <Row gutter={48}>
          <Box
            as={Col}
            sm={17}
            overflowY="auto"
            paddingBottom={16}
            paddingTop={4}
          >
            <FracaoTable
              key={tableKey}
              control={control}
              data={filteredUnidades}
              searchValue={search}
              onSearch={onSearch}
              enabledEdit={_enabledEdit}
              isLoading={_isLoadingData}
            />
          </Box>
          <Box as={Col} sm={7} paddingTop={4}>
            <Text fontSize="16px" color="gray.700" fontWeight="500">
              Soma das frações
            </Text>

            <Box
              mx="auto"
              h={280}
              bg={colors.blue200}
              mt={16}
              rounded="full"
              w={280}
              p={6}
            >
              {!_isLoadingData ? (
                <FracaoChart
                  remainingValue={remainingValue}
                  sumValues={sumValues}
                />
              ) : (
                <Skeleton w="100%" h="100%" rounded="full" />
              )}
            </Box>

            {!_isLoadingData ? (
              <>
                <FracaoChartLegend
                  sumValues={sumValues}
                  remainingValue={remainingValue}
                />

                {remainingValue > 0 ? (
                  <OutlineButton
                    mx="auto"
                    mt={8}
                    onClick={() => splitValueEqually(remainingValue)}
                    h="40px"
                  >
                    Distribuir {applyPrecision(remainingValue, 8)}% igualmente
                  </OutlineButton>
                ) : null}

                {isOverLimit ? (
                  <>
                    <Text
                      mt={8}
                      color={colors.red}
                      fontWeight="700"
                      fontSize={16}
                      textAlign="center"
                    >
                      A distribuição ultrapassa 100%.
                    </Text>
                    <Text
                      color={colors.red}
                      fontWeight="700"
                      fontSize={16}
                      textAlign="center"
                    >
                      Redistribua o valor para poder salvar.
                    </Text>
                  </>
                ) : null}
              </>
            ) : null}
          </Box>
        </Row>

        <Box
          pt="32px"
          justifyContent="flex-end"
          d="flex"
          gridGap="8px"
          flexDir="row"
          marginTop="auto"
          pb={"16px"}
        >
          <OutlineButton
            onClick={() => {
              onClose?.();
              reset();
            }}
          >
            Cancelar
          </OutlineButton>

          <PrimaryButton
            type="button"
            isLoading={isLoadingMutate}
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isValid || isOverLimit || isLoadingMutate}
            minWidth={"120px"}
          >
            Salvar
          </PrimaryButton>
        </Box>
      </Flex>
    </form>
  );
}

export default FormFracao;
